import {
  Button,
  Checkbox,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { timeFormatToYearSecond } from '@/utils/date';
import { useRequest, useSetState } from 'ahooks';
import { companyApi, CompanyVo, ETaskSource, phraseApi, taskApi } from '@/axios/company';
import CommonModalFactory from '@/plugins/ModalFactory';
import { useEffect } from 'react';
import { useStore } from '@/stores';
import SearchInput from '@/components/SearchInput';
import { openDeleteModal } from '@/components/Modal/common';
import { EAnalysisStrategy, EAutoUpdateType, ESpiderStatus, spiderApi, SpiderSchedulePageItem } from '@/axios/spider';
import { EAnalysisReportType } from '@/axios/analysis';
import PlatformItem from '@/components/Form/PlatformItem';
import { IPlatformItem } from '@/axios/common';
import AccessButton from '@/components/AccessButton';
import OperatorBtns from '@/components/Table/operateBtns';
import CommonTable from '@/components/Table';
import CustomTreeSelect from '@/components/Base/TreeSelect';
import { EPublishStatus, isPublishedStatus } from '@/constants/geo';
import dayjs from 'dayjs';

const { Text } = Typography;
const IntervalMonitorTable = ({
  companyInfo,
  refreshCompanyInfo,
}: {
  companyInfo?: CompanyVo;
  refreshCompanyInfo?: () => void;
}) => {
  const id = new URLSearchParams(window.location.search).get('companyId');
  const appStore = useStore('app');
  const [searchParams, setSearchParams] = useSetState({
    pageIndex: 1,
    pageSize: 10,
    companyId: id || '',
    search: '',
  });
  const {
    data: list,
    loading,
    runAsync: refreshList,
  } = useRequest(
    () => {
      return spiderApi.list(searchParams);
    },
    {
      refreshDeps: [searchParams],
    }
  );
  const columns: ColumnsType<SpiderSchedulePageItem> = [
    {
      title: '任务名称',
      dataIndex: 'scheduleName',
      ellipsis: true,
      width: 130,
      render: (value, _) => {
        return (
          <Text
            ellipsis={{
              tooltip: value || '-',
            }}
          >
            {value || '-'}
          </Text>
        );
      },
    },
    {
      title: '重复周期',
      dataIndex: 'intervalDays',
      width: 100,
      render: (val) => `每隔${val}天`,
    },
    {
      title: '搜索词个数',
      dataIndex: 'wordCount',
      width: 100,
    },
    {
      title: '扩展词个数',
      dataIndex: 'expandCount',
      width: 100,
    },
    {
      title: '搜索词组名称',
      dataIndex: 'componentWords',
      width: 200,
      ellipsis: true,
      render: (value) => <Tooltip title={<div>{value.join('、')}</div>}>{value.join('、')}</Tooltip>,
    },
    {
      title: '下次执行时间',
      dataIndex: 'nextRunTime',
      width: 120,
      render: (value) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>{timeFormatToYearSecond(value).split(' ').join('\n')}</div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      width: 120,
      render: (value) => (
        <div style={{ whiteSpace: 'pre-wrap' }}>{timeFormatToYearSecond(value).split(' ').join('\n')}</div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 140,
      render: (value, _) => {
        if (value === ESpiderStatus.Enable) {
          return (
            <Tag color="#36B274" variant="filled">
              运行中
            </Tag>
          );
        }
        if (value === ESpiderStatus.Disable) {
          return (
            <Tag color="#F2883D" variant="filled">
              暂停
            </Tag>
          );
        }
        return '-';
      },
    },
    {
      title: '操作',
      dataIndex: 'op',
      width: 250,
      render: (_, record) => {
        return (
          <OperatorBtns
            btns={[
              {
                type: 'edit',
                onClick: async () => {
                  openCreateIntervalMonitorTaskModal(
                    {
                      companyId: id || '',
                      platformList: appStore.platformList,
                      scheduleId: record.scheduleId,
                    },
                    {
                      title: '编辑定时监控任务',
                      okText: '保存',
                    },
                    () => {
                      setSearchParams({ pageIndex: 1 });
                      message.success('编辑成功');
                    }
                  );
                },
                permission: ['company:task:schedule:edit'],
              },
              {
                type: 'common',
                text: record.status === ESpiderStatus.Enable ? '暂停' : '恢复',
                onClick: async () => {
                  if (record.status === ESpiderStatus.Enable) {
                    await spiderApi.pause({ scheduleIds: [record.scheduleId] });
                    message.success('暂停成功');
                  } else {
                    await spiderApi.resume({ scheduleIds: [record.scheduleId] });
                    message.success('恢复成功');
                  }
                  refreshList();
                },
                permission: ['company:task:schedule:edit'],
              },
              {
                type: 'delete',
                onClick: () => {
                  openDeleteModal(
                    {
                      content: '删除定时监控任务',
                      api: () => spiderApi.delete({ scheduleIds: [record.scheduleId] }),
                    },
                    undefined,
                    refreshList
                  );
                },
                permission: ['company:task:schedule:delete'],
              },
            ]}
          />
        );
      },
    },
  ];
  return (
    <Flex vertical gap={20}>
      <Flex gap={8} align="center" justify="space-between">
        <AccessButton permission={['company:task:schedule:create']}>
          <Button
            type="primary"
            onClick={() => {
              openCreateIntervalMonitorTaskModal(
                {
                  companyId: id || '',
                  platformList: appStore.platformList,
                },
                undefined,
                () => {
                  setSearchParams({ pageIndex: 1 });
                  message.success('创建成功');
                }
              );
            }}
          >创建定时任务</Button>
        </AccessButton>

        <AccessButton permission={['company:view:update_report']}>
          <Button
            style={{ marginLeft: 'auto' }}
            type="primary"
            onClick={() => {
              openUpdateReportDataModal(
                {
                  companyId: companyInfo?.companyId || '',
                  selected: companyInfo?.recordIds,
                  platforms: companyInfo?.platforms,
                  platformList: appStore.platformList,
                },
                undefined,
                () => {
                  message.success('更新成功');
                  refreshCompanyInfo?.();
                }
              );
            }}
          >
            更新客户报表
          </Button>
        </AccessButton>
        <SearchInput
          style={{ width: '200px' }}
          placeholder="搜索任务名称"
          onSearch={(val) => {
            setSearchParams({ search: val, pageIndex: 1 });
          }}
        />
      </Flex>
      <CommonTable<SpiderSchedulePageItem>
        loading={loading}
        rowKey={'scheduleId'}
        columns={columns}
        dataSource={list?.records || []}
        total={list?.total}
        params={searchParams}
        setParams={setSearchParams}
      />
    </Flex>
  );
};
export default IntervalMonitorTable;

export const openCreateIntervalMonitorTaskModal = CommonModalFactory<{
  companyId: string;
  platformList: IPlatformItem[];
  searchWordArray?: {
    label: string;
    value: string;
  }[];
  expandWordArray?: {
    label: string;
    value: string;
  }[];
  scheduleDetail?: SpiderSchedulePageItem;
}>(
  {
    title: '创建定时监控任务',
    width: 520,
    okText: '创建',
    styles: {
      body: {
        maxHeight: '70vh',
        overflowY: 'auto',
      },
    },
  },
  ({ data, setOkFn }) => {
    const [form] = Form.useForm();
    useEffect(() => {
      if (data?.scheduleDetail) {
        form.setFieldsValue({
          scheduleName: data?.scheduleDetail?.scheduleName,
          platforms: data?.scheduleDetail?.platforms,
          componentWords: data?.scheduleDetail?.componentWords,
          expandWords: data?.scheduleDetail?.expandWords,
          analysisStrategy: data?.scheduleDetail?.analysisStrategy ?? EAnalysisStrategy.None,
          autoUpdateBoard: data?.scheduleDetail?.updateMode,
          intervalDays: data?.scheduleDetail?.intervalDays,
          autoAddMonitor: data?.scheduleDetail?.autoAddMonitor,
          autoGenerateArticle: data?.scheduleDetail?.autoGenerateArticle,
          nextRunTime: dayjs(data?.scheduleDetail?.nextRunTime),
          analysisGenerateFrequency: data?.scheduleDetail?.analysisGenerateFrequency ?? 4,
          reportCycle: data?.scheduleDetail?.reportCycle || EAnalysisReportType.Unset,
          publishToClient: isPublishedStatus(data?.scheduleDetail?.publishStatus)
            ? EPublishStatus.PUBLISHED
            : EPublishStatus.UNPUBLISHED,
          reportAutoPublishToClient: data?.scheduleDetail?.reportAutoPublishToClient || ESpiderStatus.Disable,
          reportAutoUpdateClientReport: data?.scheduleDetail?.reportAutoUpdateClientReport || ESpiderStatus.Disable,
        });
      }
    }, [data?.scheduleDetail]);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        const submitValues = {
          ...values,
          reportCycle:
            values.analysisStrategy === EAnalysisStrategy.None ? EAnalysisReportType.Unset : values.reportCycle,
          reportAutoPublishToClient:
            values.analysisStrategy === EAnalysisStrategy.None
              ? ESpiderStatus.Disable
              : values.reportAutoPublishToClient,
          reportAutoUpdateClientReport:
            values.analysisStrategy === EAnalysisStrategy.None
              ? ESpiderStatus.Disable
              : values.reportAutoUpdateClientReport,
        };
        if (data?.scheduleDetail) {
          // 编辑
          await spiderApi.update({
            scheduleId: data?.scheduleDetail?.scheduleId as string,
            companyId: data?.companyId as string,
            ...submitValues,
            nextRunTime: dayjs(submitValues.nextRunTime).format('YYYY-MM-DDTHH:mm:ss'),
          });
          return;
        }
        await spiderApi.create({
          companyId: data?.companyId as string,
          ...submitValues,
          nextRunTime: dayjs(submitValues.nextRunTime).format('YYYY-MM-DDTHH:mm:ss'),
        });
      });
    }, [data?.companyId, data?.scheduleDetail]);
    const analysisStrategy = Form.useWatch('analysisStrategy', form) ?? EAnalysisStrategy.None;
    return (
      <div>
        <Form
          form={form}
          initialValues={{
            analysisStrategy: EAnalysisStrategy.None,
            autoUpdateBoard: EAutoUpdateType.None,
            componentWords: [],
            expandWords: [],
            intervalDays: 7,
            autoGenerateArticle: false,
            autoAddMonitor: false,
            // 默认当前时间的五分钟后
            nextRunTime: dayjs().add(5, 'minute'),
            analysisGenerateFrequency: 4,
            reportCycle: EAnalysisReportType.Monthly,
            publishToClient: EPublishStatus.UNPUBLISHED,
            reportAutoPublishToClient: ESpiderStatus.Disable,
            reportAutoUpdateClientReport: ESpiderStatus.Disable,
          }}
          colon={false}
          layout="vertical"
        >
          <Form.Item name="scheduleName" label="任务名称" rules={[{ required: true, message: '请输入任务名称' }]}>
            <Input placeholder="请输入任务名称" />
          </Form.Item>


          <PlatformItem platformList={data?.platformList} />
          <Form.Item label="定时">
            <Space>
              <Form.Item name="nextRunTime" label="" noStyle>
                <DatePicker showTime />
              </Form.Item>
              每隔
              <Form.Item name="intervalDays" noStyle>
                <InputNumber min={1} step={1} precision={0} />
              </Form.Item>
              天
            </Space>
          </Form.Item>
          <Form.Item name="componentWords" label="搜索词组" rules={[{ required: true, message: '请选择搜索词组' }]}>
            <CustomTreeSelect treeData={data?.searchWordArray} treeCheckable placeholder="请选择搜索词组" />
          </Form.Item>
          <Form.Item name="expandWords" label="扩展规则">
            <CustomTreeSelect treeData={data?.expandWordArray} treeCheckable placeholder="请选择扩展规则" />
          </Form.Item>
          <Form.Item name="analysisStrategy" label="是否需要分析">
            <Radio.Group
              options={[
                {
                  value: EAnalysisStrategy.None,
                  label: '否',
                },
                {
                  value: EAnalysisStrategy.pre_optimization,
                  label: '优化前分析',
                },
                {
                  value: EAnalysisStrategy.post_optimization,
                  label: '优化后分析',
                },
              ]}
            />
          </Form.Item>

          {analysisStrategy !== EAnalysisStrategy.None && (
            <>
              <Form.Item label="">
                <Space>
                  每
                  <Form.Item name="analysisGenerateFrequency" noStyle>
                    <InputNumber min={1} step={1} precision={0} />
                  </Form.Item>
                  次任务生成一次分析报告
                  <Form.Item name="reportCycle" noStyle>
                    <Select
                      style={{ width: 120 }}
                      options={[
                        {
                          value: EAnalysisReportType.Unset,
                          label: '未设置',
                        },
                        {
                          value: EAnalysisReportType.Daily,
                          label: '日报',
                        },
                        {
                          value: EAnalysisReportType.Weekly,
                          label: '周报',
                        },
                        {
                          value: EAnalysisReportType.Monthly,
                          label: '月报',
                        },
                      ]}
                    />
                  </Form.Item>
                </Space>
              </Form.Item>
              <Form.Item
                name="reportAutoPublishToClient"
                getValueProps={(value) => ({ checked: value === ESpiderStatus.Enable })}
                getValueFromEvent={(e) => (e?.target?.checked ? ESpiderStatus.Enable : ESpiderStatus.Disable)}
              >
                <Checkbox>分析报告生成后自动发布到客户后台</Checkbox>
              </Form.Item>
              <Form.Item
                name="reportAutoUpdateClientReport"
                getValueProps={(value) => ({ checked: value === ESpiderStatus.Enable })}
                getValueFromEvent={(e) => (e?.target?.checked ? ESpiderStatus.Enable : ESpiderStatus.Disable)}
              >
                <Checkbox>分析报告生成后自动更新到客户报表</Checkbox>
              </Form.Item>
            </>
          )}
          <Form.Item name="autoAddMonitor" label="是否添加监控">
            {/* <Switch /> */}
            <Radio.Group
              options={[
                {
                  value: true,
                  label: '是',
                },
                {
                  value: false,
                  label: '否',
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="autoGenerateArticle" label="是否自动生成文章">
            {/* <Switch /> */}
            <Radio.Group
              options={[
                {
                  value: true,
                  label: '是',
                },
                {
                  value: false,
                  label: '否',
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="autoUpdateBoard" label="是否自动更新至客户看板">
            <Radio.Group
              options={[
                {
                  value: EAutoUpdateType.None,
                  label: '不更新',
                },
                {
                  value: EAutoUpdateType.Increment,
                  label: '增量更新',
                },
                {
                  value: EAutoUpdateType.Overwrite,
                  label: '覆盖更新',
                },
              ]}
            />
          </Form.Item>
          <Form.Item name="publishToClient" label="是否自动更新至客户后台">
            <Radio.Group
              options={[
                {
                  value: EPublishStatus.PUBLISHED,
                  label: '是',
                },
                {
                  value: EPublishStatus.UNPUBLISHED,
                  label: '否',
                },
              ]}
            />
          </Form.Item>
        </Form>
      </div>
    );
  },
  async ({ companyId, platformList, ...props }) => {
    const res = await phraseApi.list({
      pageIndex: 1,
      pageSize: 100,
      companyId,
      type: 'SEARCH_WORD',
    });
    const expandList = await phraseApi.list({
      pageIndex: 1,
      pageSize: 100,
      companyId,
      type: 'EXPAND_WORD',
    });
    let scheduleDetail = null;
    if (props?.scheduleId) {
      scheduleDetail = await spiderApi.get(props?.scheduleId || '');
    }
    return {
      ...props,
      companyId,
      platformList,
      searchWordArray: (res.records || []).map((item) => ({
        label: item.name as string,
        value: item.recordId as string,
      })),
      expandWordArray: (expandList.records || []).map((item) => ({
        label: item.name as string,
        value: item.recordId as string,
      })),
      scheduleDetail,
    };
  }
);

export const openUpdateReportDataModal = CommonModalFactory<{
  companyId?: string;
  taskList: { label: string; value: string }[];
  selected?: string[];
  platforms?: string[];
  platformList?: IPlatformItem[];
}>(
  {
    title: '更新报表数据',
    width: 514,
    okText: '更新',
    maskClosable: false,
  },
  ({ data, setOkFn }) => {
    const { taskList, selected } = data as { taskList: { label: string; value: string }[]; selected?: string[] };
    const [form] = Form.useForm();
    useEffect(() => {
      form.setFieldsValue({
        taskIds: selected || [],
        platforms: data?.platforms || [],
      });
    }, [data?.selected, data?.platforms]);
    useEffect(() => {
      setOkFn(async () => {
        const values = await form.validateFields();
        await companyApi.updateReport({
          companyId: data?.companyId || '',
          ...values,
        });
      });
    }, [data?.companyId]);
    return (
      <Form form={form} layout="vertical">
        <Form.Item name="taskIds" label="任务" rules={[{ required: true, message: '请至少选择一个任务' }]}>
          <CustomTreeSelect
            style={{
              width: '100%',
            }}
            treeCheckable
            placeholder="请至少选择一个任务"
            treeData={taskList}
          />
        </Form.Item>
        <PlatformItem platformList={data?.platformList} />
      </Form>
    );
  },
  async ({ companyId, ...props }) => {
    const res = await taskApi.list({
      pageIndex: 1,
      pageSize: 1000,
      taskId: companyId,
      source: ETaskSource.Company,
    });
    return {
      ...props,
      taskList: res.records?.map((item) => ({ label: item.taskName || item.id, value: item.id })),
      companyId,
    };
  }
);
